/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { TbSelector } from 'react-icons/tb';

import { Listbox, Transition } from '@headlessui/react';

import Typography from 'components-new/typography/typography';
import Icon from 'components-new/icon/icon';
import Checkbox from 'components-new/checkbox/checkbox';
import type { EmptyStateProps } from 'components-new/empty-state/empty-state';
import EmptyState from 'components-new/empty-state/empty-state';

export type SelectOption = {
  label: string;
  value: string;
};

export interface SelectInputProps {
  title?: string;
  name?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string | string[];
  multiple?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  optionClassName?: string;
  optionsListClassName?: string;
  errorClassName?: string;
  onSelectedOptionsChange?: (values: SelectOption | SelectOption[]) => void;
  emptyStateProps?: EmptyStateProps;
}

const SelectInput: React.FC<SelectInputProps> = ({
  title,
  name,
  placeholder,
  options,
  value,
  multiple,
  error,
  disabled,
  className,
  optionClassName,
  optionsListClassName,
  errorClassName,
  onSelectedOptionsChange,
  emptyStateProps,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const isSelected = (val: SelectOption['value']) => {
    return !!selectedOptions.includes(val);
  };

  const containerClassName = twMerge('space-y-1', className);

  const optionClassNames = twMerge(
    'relative group cursor-default select-none py-1 pl-8 pr-4 mx-1.5 rounded hover:bg-background-hover focus:bg-background-hover',
    optionClassName,
  );

  const optionsListClassNames = twMerge(
    'max-h-60 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5',
    optionsListClassName,
  );

  const listBoxButtonClassName = twMerge(
    'relative h-[34px] text-start cursor-default',
    'rounded-md bg-input-idle w-full px-3',
    'transition ease-in-out duration-200',
    'border focus-visible:outline-none',
    !disabled && 'hover:bg-input-hover focus-within:bg-input-active',
    !disabled &&
      !error &&
      `border-transparent
       active:border-primary active:outline active:outline-primary-200 active:input-idle active:bg-input-active
       focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200
       focus-within:ring-2 focus-within:ring-primary-200
       focus:border-primary`,
    error &&
      'border border-destructive-300 focus-within:ring-2 focus-within:ring-destructive-100',
  );

  const errorClassNames = twMerge('text-destructive-300 mt-1', errorClassName);

  const getSelectedOptions = useCallback(
    (values: string[] | string) => {
      // if multiple === true, values is an array, otherwise it is a string
      if (typeof values === 'string') {
        return options.find(
          (option) => option.value === values,
        ) as SelectOption;
      }

      return options.filter((option) => values.includes(option.value));
    },
    [options],
  );

  const handleValuesChange = useCallback(
    (values: string[] | string) => {
      // if multiple === true, values is an array, otherwise it is a string
      if (typeof values === 'string') {
        setSelectedOptions([values]);
      } else {
        setSelectedOptions(values);
      }
      onSelectedOptionsChange?.(getSelectedOptions(values));
    },
    [getSelectedOptions, onSelectedOptionsChange],
  );

  const buttonContent = useMemo(() => {
    let content;
    if (!selectedOptions || !selectedOptions.length) {
      content = placeholder;
    } else if (typeof selectedOptions === 'string') {
      content = options.find(
        (option) => option.value === selectedOptions,
      )?.label;
    } else {
      content = options
        .filter((option) => selectedOptions.includes(option.value))
        .map((option) => option.label)
        .join(', ');
    }
    return (
      <Typography
        variant="ui-input"
        as="span"
        className={twMerge(
          'text-start w-full truncate !font-light',
          disabled
            ? 'text-gray-200'
            : content === placeholder && 'text-gray-400',
        )}
      >
        {content}
      </Typography>
    );
  }, [disabled, options, placeholder, selectedOptions]);

  useEffect(() => {
    if (value) setSelectedOptions(Array.isArray(value) ? value : [value]);
  }, [value]);

  return (
    <Listbox
      as="div"
      className={containerClassName}
      value={selectedOptions}
      multiple={multiple}
      onChange={handleValuesChange}
      name={name}
    >
      {() => (
        <>
          {title && (
            <Typography as="p" variant="body-medium-bold" className="mb-2">
              {title}
            </Typography>
          )}
          <div className="relative appearance-none">
            <Listbox.Button className={listBoxButtonClassName}>
              <div className="flex items-center">
                {buttonContent}
                <span className="flex items-center pointer-events-none">
                  <Icon
                    icon={TbSelector}
                    className={!disabled ? 'text-gray-400' : 'text-gray-200'}
                  />
                </span>
              </div>
            </Listbox.Button>

            <Transition
              unmount={false}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="absolute mt-1 w-full rounded-md bg-white shadow-lg z-50"
            >
              {!disabled && (
                <Listbox.Options static className={optionsListClassNames}>
                  {options.map((option) => {
                    const selected = isSelected(option.value);

                    return (
                      <Listbox.Option key={option.value} value={option.value}>
                        {({ active }) => (
                          <div
                            className={twMerge(
                              active && 'bg-background-hover',
                              optionClassNames,
                            )}
                          >
                            <Typography
                              variant="ui-input"
                              as="span"
                              className="block break-words text-xs"
                            >
                              {option.label}
                            </Typography>
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center">
                              {selected ? (
                                <Checkbox variant="checked" />
                              ) : (
                                <Checkbox variant="unchecked" />
                              )}
                            </div>
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                  <div className="flex justify-center">
                    {!options.length && <EmptyState {...emptyStateProps} />}
                  </div>
                </Listbox.Options>
              )}
            </Transition>
          </div>

          {error && (
            <Typography
              as="p"
              variant="body-medium"
              className={errorClassNames}
            >
              {error}
            </Typography>
          )}
        </>
      )}
    </Listbox>
  );
};

export default SelectInput;
